// File: ManageSubject.js
import React, { useEffect, useRef, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Table, Button, Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";

const EMPTY_SUB_FORM = {
  sub_id: null, sub_subid: "", sub_subname: "",
  sub_credit: "", sub_semester: "1", sub_type: "basic", sub_order: ""
};

export default function ManageSubject() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const t of theads) { t.classList.remove("table-secondary"); t.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const t of theads) { t.classList.remove("table-primary"); t.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();

  // ---- Year ----
  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || "");
  const [yearList, setYearList] = useState([]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => {
        const list = res.data || [];
        setYearList(list);
        const saved = localStorage.getItem("yearId");
        const initial = saved && list.find(y => String(y.year_id) === saved)
          ? saved
          : list.length ? String(list[0].year_id) : "";
        setYearId(initial);
        if (initial) localStorage.setItem("yearId", initial);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  // ---- Assign syllabus list (by year) ----
  const [assignList, setAssignList] = useState([]);
  const [selectedAssignId, setSelectedAssignId] = useState("");

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    setSelectedAssignId("");
    setSubjects([]);
    axiosInstance.get(`${process.env.REACT_APP_API}/getAssignSyllabus/${yearId}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => setAssignList(res.data || []))
      .catch(() => Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: "ไม่สามารถโหลดข้อมูลหลักสูตรที่กำหนดได้" }))
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  const selectedAssign = assignList.find(a => String(a.as_id) === String(selectedAssignId)) || null;

  // ---- Subjects ----
  const [subjects, setSubjects] = useState([]);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const subjectPanelRef = useRef(null);

  const loadSubjects = (assign) => {
    setSubjectLoading(true);
    const prog = assign.p_id ?? "null";
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getSubjectsBySyllabus/${assign.syl_id}/${assign.c_id}/${prog}`,
      { headers: { authorization: `Bearer ${getToken()}` } }
    )
      .then(res => setSubjects(res.data || []))
      .catch(() => Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: "ไม่สามารถโหลดรายวิชาได้" }))
      .finally(() => setSubjectLoading(false));
  };

  const handleSelectAssign = (as_id) => {
    setSelectedAssignId(as_id);
    if (!as_id) { setSubjects([]); return; }
    const assign = assignList.find(a => String(a.as_id) === String(as_id));
    if (assign) {
      loadSubjects(assign);
      setTimeout(() => {
        subjectPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  // ---- Subject Modal ----
  const [subjectModalShow, setSubjectModalShow] = useState(false);
  const [subjectModalMode, setSubjectModalMode] = useState("add");
  const [subForm, setSubForm] = useState(EMPTY_SUB_FORM);

  const handleOpenSubjectModal = (mode, subject = null) => {
    setSubjectModalMode(mode);
    setSubForm(mode === "add" ? EMPTY_SUB_FORM : {
      sub_id: subject.sub_id,
      sub_subid: subject.sub_subid,
      sub_subname: subject.sub_subname,
      sub_credit: subject.sub_credit,
      sub_semester: String(subject.sub_semester),
      sub_type: subject.sub_type,
      sub_order: subject.sub_order ?? ""
    });
    setSubjectModalShow(true);
  };

  const handleSaveSubject = () => {
    if (!subForm.sub_subid || !subForm.sub_subname || subForm.sub_credit === "") {
      Swal.fire("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }
    setLoading(true);
    const payload = {
      sub_subid: subForm.sub_subid,
      sub_subname: subForm.sub_subname,
      sub_credit: Number(subForm.sub_credit),
      sub_semester: Number(subForm.sub_semester),
      sub_type: subForm.sub_type,
      sub_order: subForm.sub_order !== "" ? Number(subForm.sub_order) : null
    };
    if (subjectModalMode === "add") {
      axiosInstance.post(`${process.env.REACT_APP_API}/addSubject`,
        { ...payload, syl_id: selectedAssign.syl_id, class_id: selectedAssign.c_id, program_id: selectedAssign.p_id || null },
        { headers: { authorization: `Bearer ${getToken()}` } }
      )
        .then(() => {
          setSubjectModalShow(false);
          loadSubjects(selectedAssign);
          setAssignList(assignList.map(a =>
            a.as_id === selectedAssign.as_id ? { ...a, subject_count: (a.subject_count || 0) + 1 } : a
          ));
          setLoading(false);
          Swal.fire({ icon: "success", title: "เพิ่มรายวิชาสำเร็จ", timer: 1500, showConfirmButton: false });
        })
        .catch(err => {
          setLoading(false);
          Swal.fire({ icon: "error", title: err.response?.data?.title || "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถเพิ่มรายวิชาได้" });
        });
    } else {
      axiosInstance.post(`${process.env.REACT_APP_API}/updateSubject`,
        { ...payload, sub_id: subForm.sub_id },
        { headers: { authorization: `Bearer ${getToken()}` } }
      )
        .then(() => {
          setSubjectModalShow(false);
          loadSubjects(selectedAssign);
          setLoading(false);
          Swal.fire({ icon: "success", title: "แก้ไขรายวิชาสำเร็จ", timer: 1500, showConfirmButton: false });
        })
        .catch(err => {
          setLoading(false);
          Swal.fire({ icon: "error", title: err.response?.data?.title || "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถแก้ไขรายวิชาได้" });
        });
    }
  };

  const handleDeleteSubject = (sub_id) => {
    Swal.fire({
      title: "ยืนยันการลบรายวิชา", text: "คุณต้องการลบรายวิชานี้หรือไม่?",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#d33", cancelButtonColor: "#3085d6",
      confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก"
    }).then(result => {
      if (result.isConfirmed) {
        setLoading(true);
        axiosInstance.post(`${process.env.REACT_APP_API}/deleteSubject`, { sub_id }, {
          headers: { authorization: `Bearer ${getToken()}` }
        })
          .then(() => {
            setSubjects(subjects.filter(s => s.sub_id !== sub_id));
            setAssignList(assignList.map(a =>
              a.as_id === selectedAssign.as_id ? { ...a, subject_count: Math.max(0, (a.subject_count || 1) - 1) } : a
            ));
            setLoading(false);
            Swal.fire({ icon: "success", title: "ลบรายวิชาสำเร็จ", timer: 1500, showConfirmButton: false });
          })
          .catch(err => {
            setLoading(false);
            Swal.fire({ icon: "error", title: err.response?.data?.title || "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถลบรายวิชาได้" });
          });
      }
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทะเบียนและหลักสูตร</Link></li>
              <li className="breadcrumb-item active" aria-current="page">จัดการรายวิชา</li>
            </ol>
            <h4 className="main-title mb-0">จัดการรายวิชา</h4>
          </div>
        </div>

        {/* Filter card */}
        <Card className="card-one mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="fw-semibold">ปีการศึกษา</Form.Label>
                <Form.Select
                  value={yearId}
                  onChange={e => {
                    setYearId(e.target.value);
                    localStorage.setItem("yearId", e.target.value);
                  }}
                >
                  <option value="">-- เลือกปีการศึกษา --</option>
                  {yearList.map(y => (
                    <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={8}>
                <Form.Label className="fw-semibold">กำหนดหลักสูตร</Form.Label>
                <Form.Select
                  value={selectedAssignId}
                  onChange={e => handleSelectAssign(e.target.value)}
                  disabled={!yearId || assignList.length === 0}
                >
                  <option value="">-- เลือกหลักสูตรที่กำหนด --</option>
                  {assignList.map(a => (
                    <option key={a.as_id} value={a.as_id}>
                      {a.c_class}{a.p_code ? ` (${a.p_code})` : ""} — หลักสูตรปี {a.syl_year}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Subject panel */}
        {selectedAssign && (
          <Card className="card-one" ref={subjectPanelRef}>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title as="h6" className="mb-0">
                รายวิชา —&nbsp;
                <span className="text-primary">{selectedAssign.c_class}</span>
                {selectedAssign.p_code && <span className="text-muted"> ({selectedAssign.p_code})</span>}
                <span className="text-muted fw-normal ms-1">หลักสูตรปี {selectedAssign.syl_year}</span>
                <span className="text-muted fw-normal ms-1 fs-sm">({subjects.length} วิชา)</span>
              </Card.Title>
              <Button variant="success" size="sm" onClick={() => handleOpenSubjectModal("add")}>
                <i className="ri-add-line"></i> เพิ่มรายวิชา
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {subjectLoading ? (
                <div className="text-center py-4 text-muted">กำลังโหลดรายวิชา...</div>
              ) : subjects.length === 0 ? (
                <div className="text-center py-4 text-muted">ยังไม่มีรายวิชา กดปุ่ม "เพิ่มรายวิชา" เพื่อเริ่มต้น</div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th className="text-center" style={{ width: 60 }}>ลำดับ</th>
                      <th style={{ width: 130 }}>รหัสวิชา</th>
                      <th>ชื่อวิชา</th>
                      <th className="text-center" style={{ width: 90 }}>หน่วยกิต</th>
                      <th className="text-center" style={{ width: 100 }}>ภาคเรียน</th>
                      <th className="text-center" style={{ width: 110 }}>ประเภท</th>
                      <th className="text-center" style={{ width: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((sub, idx) => (
                      <tr key={sub.sub_id}>
                        <td className="text-center text-muted">{sub.sub_order ?? idx + 1}</td>
                        <td>{sub.sub_subid}</td>
                        <td>{sub.sub_subname}</td>
                        <td className="text-center">{sub.sub_credit}</td>
                        <td className="text-center">{sub.sub_semester}</td>
                        <td className="text-center">
                          <span className={`badge ${sub.sub_type === "basic" ? "bg-primary" : "bg-warning text-dark"}`}>
                            {sub.sub_type === "basic" ? "พื้นฐาน" : "เพิ่มเติม"}
                          </span>
                        </td>
                        <td className="text-center">
                          <Button variant="secondary" size="sm" className="btn-xs" onClick={() => handleOpenSubjectModal("edit", sub)}>
                            <i className="ri-edit-line"></i>
                          </Button>
                          <Button variant="danger" size="sm" className="btn-xs ms-1" onClick={() => handleDeleteSubject(sub.sub_id)}>
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>

      {/* Subject add/edit Modal */}
      <Modal show={subjectModalShow} backdrop="static" size="md" onHide={() => setSubjectModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-book-2-line"></i> {subjectModalMode === "add" ? "เพิ่มรายวิชา" : "แก้ไขรายวิชา"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={e => e.preventDefault()}>
            <Form.Group className="mb-3">
              <Form.Label>รหัสวิชา</Form.Label>
              <Form.Control
                type="text"
                value={subForm.sub_subid}
                onChange={e => setSubForm({ ...subForm, sub_subid: e.target.value })}
                placeholder="เช่น ท21101"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อวิชา</Form.Label>
              <Form.Control
                type="text"
                value={subForm.sub_subname}
                onChange={e => setSubForm({ ...subForm, sub_subname: e.target.value })}
                placeholder="ชื่อวิชา"
              />
            </Form.Group>
            <Row className="mb-3">
              <Col>
                <Form.Label>หน่วยกิต</Form.Label>
                <Form.Control
                  type="number" step="0.5" min="0"
                  value={subForm.sub_credit}
                  onChange={e => setSubForm({ ...subForm, sub_credit: e.target.value })}
                  placeholder="0.5"
                />
              </Col>
              <Col>
                <Form.Label>ลำดับ</Form.Label>
                <Form.Control
                  type="number" min="0"
                  value={subForm.sub_order}
                  onChange={e => setSubForm({ ...subForm, sub_order: e.target.value })}
                  placeholder="0"
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Label>ภาคเรียน</Form.Label>
                <Form.Select value={subForm.sub_semester} onChange={e => setSubForm({ ...subForm, sub_semester: e.target.value })}>
                  <option value="1">ภาคเรียนที่ 1</option>
                  <option value="2">ภาคเรียนที่ 2</option>
                </Form.Select>
              </Col>
              <Col>
                <Form.Label>ประเภท</Form.Label>
                <Form.Select value={subForm.sub_type} onChange={e => setSubForm({ ...subForm, sub_type: e.target.value })}>
                  <option value="basic">พื้นฐาน</option>
                  <option value="extra">เพิ่มเติม</option>
                </Form.Select>
              </Col>
            </Row>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSubjectModalShow(false)}>ปิด</Button>
          <Button variant="success" onClick={handleSaveSubject}>
            {subjectModalMode === "add" ? "บันทึก" : "บันทึกการแก้ไข"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
